<script lang="ts">
  // Reusable slider+editable-number combo.
  // - Drag the slider OR click the number to type a value directly
  // - Out-of-range values are clamped on input
  // - Optional decimal precision auto-detected from `step`
  interface Props {
    label: string;
    unit?: string;
    min: number;
    max: number;
    step: number;
    value: number;
    accent?: string;
    onInput: (v: number) => void;
  }
  const { label, unit, min, max, step, value, accent, onInput }: Props = $props();

  const decimals = $derived(step < 1 ? Math.max(0, Math.ceil(-Math.log10(step))) : 0);

  function commit(raw: string) {
    const v = parseFloat(raw);
    if (!isFinite(v)) return;
    onInput(Math.max(min, Math.min(max, v)));
  }
</script>

<label class="ns" style={accent ? `--ns-accent:${accent}` : ''}>
  <span class="ns-row">
    <span class="ns-lab">{label}{#if unit} <em>{unit}</em>{/if}</span>
    <input type="number" class="ns-num"
      {min} {max} {step}
      value={value.toFixed(decimals)}
      oninput={(e) => commit((e.currentTarget as HTMLInputElement).value)} />
  </span>
  <input type="range" class="ns-range"
    {min} {max} {step} {value}
    oninput={(e) => commit((e.currentTarget as HTMLInputElement).value)} />
</label>

<style>
  .ns {
    --ns-accent: var(--accent);
    display: grid; grid-template-rows: auto auto;
    row-gap: 3px; column-gap: 6px;
    font-size: 11px; color: var(--text-3);
  }
  .ns-row { display: flex; align-items: center; gap: 6px; min-width: 0; }
  .ns-lab {
    flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .ns-lab em {
    color: var(--text-muted); font-size: 9px; font-style: normal;
    margin-left: 4px; padding: 1px 5px;
    background: var(--chip-bg); border-radius: 3px;
    letter-spacing: 0.04em;
  }
  .ns-num {
    width: 48px;
    background: transparent;
    border: 1px solid transparent;
    color: var(--ns-accent);
    text-align: right;
    font-weight: 600;
    font-family: inherit;
    font-size: 11px;
    padding: 1px 3px;
    border-radius: 3px;
    font-variant-numeric: tabular-nums;
    -moz-appearance: textfield;
  }
  .ns-num::-webkit-inner-spin-button,
  .ns-num::-webkit-outer-spin-button {
    -webkit-appearance: none; margin: 0;
  }
  .ns-num:hover { background: var(--chip-bg); border-color: var(--border-soft); }
  .ns-num:focus { outline: none; background: var(--bg-viewer-from); border-color: var(--ns-accent); }
  .ns-range {
    width: 100%; accent-color: var(--ns-accent);
    height: 18px; margin: 0;
  }
</style>
